import { Blueprint, Transaction, HttpTransaction } from 'types/rest/assetCatalogTypes';
import { BlueprintsFilter } from './blueprintsTypes';

export interface TransactionSummary {
  method: string;
  url: string;
  status: number;
}

export const transactionSummary = (txn: Transaction): TransactionSummary => {
  if (txn.type === 'HTTP') {
    const http = txn as HttpTransaction;
    return { method: http.dsl.requestDsl.method, url: http.dsl.requestDsl.path, status: http.dsl.responseDsl.status };
  }
  return { method: 'MSG', url: txn.name, status: 0 };
};

export interface BlueprintCounts {
  all: number;
  builtIn: number;
  account: number;
  workspace: number;
  private: number;
}

export const filterBlueprints = (list: Blueprint[], filter: BlueprintsFilter): Blueprint[] => {
  const term = filter.searchTerm.trim().toLowerCase();
  return list.filter(bp => {
    if (filter.source === 'BUILT_IN' && bp.source !== 'BUILT_IN') return false;
    if (filter.source === 'CUSTOM' && bp.source !== 'CUSTOM') return false;

    if (term) {
      const haystack = `${bp.name} ${bp.description} ${bp.tags.join(' ')}`.toLowerCase();
      if (!haystack.includes(term)) return false;
    }

    if (filter.tags.length > 0 && !filter.tags.some(t => bp.tags.includes(t))) return false;

    return true;
  });
};

// Brand palette (from the blaze-bootstrap theme) used for generated blueprint thumbnails.
// All are dark enough to read white text on. Yellow is intentionally excluded.
const AVATAR_PALETTE = [
  '#5405FF', // primary
  '#0c7bb3', // info
  '#149E0F', // success
  '#BC8300', // warning
  '#D11323', // danger
  '#6610f2', // indigo
  '#e83e8c', // pink
  '#20c997', // teal
  '#fd7e14', // orange
  '#17a2b8', // cyan
];

/**
 * Deterministic background color for a blueprint's generated thumbnail, derived from its name.
 * Same name always yields the same color (stable across refreshes), but names spread across
 * the palette so adjacent tiles differ.
 */
export const blueprintAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

export const getBlueprintCounts = (list: Blueprint[]): BlueprintCounts => ({
  all: list.length,
  builtIn: list.filter(b => b.source === 'BUILT_IN').length,
  account: list.filter(b => b.source === 'CUSTOM' && b.visibility === 'ACCOUNT').length,
  workspace: list.filter(b => b.source === 'CUSTOM' && b.visibility === 'WORKSPACE').length,
  private: list.filter(b => b.source === 'CUSTOM' && b.visibility === 'PRIVATE').length,
});

/**
 * Distinct tags across all blueprints, ordered by descending frequency.
 * Ties are broken alphabetically so the ordering is stable across refreshes.
 */
export const getTagsByFrequency = (list: Blueprint[]): string[] => {
  const counts = new Map<string, number>();
  list.forEach(bp => {
    bp.tags.forEach(tag => {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    });
  });
  return Array.from(counts.keys()).sort((a, b) => {
    const diff = (counts.get(b) as number) - (counts.get(a) as number);
    return diff !== 0 ? diff : a.localeCompare(b);
  });
};
